import { useMutation, useQueryClient } from '@tanstack/react-query'

import { toApiError, type ApiError } from '@/lib/api/errors'
import { saveCourtPriceBands } from '@/lib/venue/priceApi'
import type { CourtPriceBandSaveInput, CourtPriceBandView } from '@/lib/venue/priceTypes'
import type { BulkApplyResult, BulkApplyTarget } from '@/lib/venue/priceViewModel'

import { courtPriceBandsQueryKey } from './useCourtPriceBands'
import { courtsQueryKey } from './useCourts'

export interface SavePriceBandsVariables {
  input: CourtPriceBandSaveInput
  /** 일괄 적용 대상(현재 코트는 제외 — 항상 먼저 저장된다). 비어 있으면 단건 저장. */
  targets: BulkApplyTarget[]
}

export interface SavePriceBandsOutcome {
  /** 현재 코트의 저장 응답(= `GET` 과 동형). */
  view: CourtPriceBandView
  /** 현재 코트를 **포함한** 전체 결과. 실패해도 앞선 성공은 되돌리지 않는다(설계 D2·R3). */
  results: BulkApplyResult[]
  /** 성공한 코트들의 응답 — `onSuccess` 가 각 코트 캐시에 그대로 꽂는다. */
  savedViews: CourtPriceBandView[]
}

/**
 * 코트 요일유형 요금 저장 + **일괄 적용 팬아웃**(#852 §2-2, 설계 요구 1).
 *
 * ① **현재 코트를 가장 먼저** 저장한다 — 실패하면 여기서 예외를 던져 화면이 평소처럼 오류를 보여준다.
 * ② 나머지 대상은 **순차** 저장한다. 서버가 요금 저장 시 구장 행 잠금을 잡아 어차피 직렬화되므로
 *    (`#866` R10) 병렬 발사는 대기·타임아웃만 늘린다.
 * ③ 성공한 코트마다 응답(= `GET` 과 동형)을 그 코트의 캐시에 **직접 반영**한다 — 재조회하지 않는다
 *    (`#852` §2-2 "저장 후 재조회가 필요 없다").
 * ④ 코트 목록 쿼리는 무효화한다(`hasPriceBands`·요금 요약이 목록에도 실린다 — `#852` §4-2).
 *
 * 🚨 저장 EP 가 멱등이라(같은 값이면 저장하지 않는다) 실패분 재시도가 현재 코트를 다시 포함해도 안전하다.
 */
export function useSaveCourtPriceBands(venueId: string, courtId: number) {
  const queryClient = useQueryClient()

  return useMutation<SavePriceBandsOutcome, ApiError, SavePriceBandsVariables>({
    mutationFn: async ({ input, targets }) => {
      const view = await saveCourtPriceBands(venueId, courtId, input)
      const results: BulkApplyResult[] = [{ courtId, name: view.courtName, ok: true }]
      const savedViews: CourtPriceBandView[] = [view]

      for (const target of targets) {
        try {
          const saved = await saveCourtPriceBands(venueId, target.courtId, input)
          savedViews.push(saved)
          results.push({ ...target, ok: true })
        } catch (error) {
          results.push({ ...target, ok: false, message: toApiError(error).message })
        }
      }

      return { view, results, savedViews }
    },
    onSuccess: ({ savedViews }) => {
      for (const saved of savedViews) {
        queryClient.setQueryData(courtPriceBandsQueryKey(venueId, saved.courtId), saved)
      }
      void queryClient.invalidateQueries({ queryKey: courtsQueryKey(venueId) })
    },
  })
}
